export default {
	title: '{{brandName}}',
	welcome: 'Welcome to {{brandName}}',
	diagram: 'Diagram',
	language: 'Language',
	english: 'English',
	spanish: 'Spanish',
	// Canonical public CTA keys (PRD 039). Always resolved through
	// app/utils/pz/cta/resolvePublicCtaKey.ts; do not add per-surface variants.
	cta: {
		startFree: 'Start free',
		createAccountAndStart: 'Create your account and start here',
		unlockCourse:
			'Unlock {{mainEntityDemonstrativeLower}} {{mainEntityLabelLower}}',
		unlockCollection:
			'Unlock {{collectionEntityDemonstrativeLower}} {{collectionEntityLabelLower}}',
	},
	docs: {
		onThisPage: 'On this page',
		search: {
			placeholder: 'Search by title...',
		},
		navigation: {
			open: 'Browse pages',
			noResults: 'No pages match your search.',
		},
		public: {
			title: 'Help',
			groups: {
				startHere: 'Start Here',
				coreConcepts: 'Core Concepts',
				editorialSystem: 'Editorial System',
				pedagogicalPrinciples: 'Pedagogical Principles',
				productGuides: 'Product Guides',
				commonErrors: 'Common Errors',
			},
			relatedArticles: 'Related articles',
			semanticSearch: 'Semantic search',
			semanticSearchDescription:
				'Find answers across the full documentation using intent-aware ranking.',
			searchNoResults: 'No documentation pages match your query yet.',
			searchHint: 'Type a question or keywords to search the help center.',
		},
		private: {
			searchPlaceholder: 'Search internal docs...',
		},
		meta: {
			owner: 'Owner',
			status: 'Status',
			lastReviewed: 'Last reviewed',
			statusDraft: 'Draft',
			statusActive: 'Active',
			statusDeprecated: 'Deprecated',
		},
		feedback: {
			titleA: 'Did this page solve your case?',
			titleB: 'Was this article helpful for your workflow?',
			question: 'Your feedback helps us prioritize documentation improvements.',
			helped: 'Yes, it helped',
			notHelped: 'Not yet',
			thanks: 'Thanks for your feedback.',
			contactCta: 'Contact support for this case',
		},
		dashboard: {
			title: 'Public docs health dashboard',
			description:
				'Operational snapshot of freshness, ownership, and support intent coverage.',
			generatedAt: 'Generated at',
			totalDocs: 'Total docs',
			docsWithoutOwner: 'Docs without owner',
			staleDocs: 'Stale docs',
			noSupportIntent: 'Docs without support intent',
			noFeedbackData:
				'Low-utility pages should be reviewed alongside docs_page_view, docs_scroll_depth, and search behavior.',
			tableTitle: 'Detailed status',
			table: {
				path: 'Path',
				source: 'Source',
				owner: 'Owner',
				status: 'Status',
				lastReviewed: 'Last reviewed',
				cadence: 'Cadence (days)',
				freshness: 'Freshness',
			},
			sourceBrand: 'Brand',
			sourceGlobal: 'Global',
			freshnessOk: 'On time',
			freshnessStale: 'Review needed',
			sopLink: 'Open monthly review SOP',
			noData: 'No public docs data available for this brand.',
		},
	},
	changeLanguage: 'Change language',
	settings: {
		title: 'Settings',
		general: 'General',
		description: 'Manage your account settings.',
		mobileSelectPlaceholder: 'Select a settings section',
		tabs: {
			profile: 'Profile',
			account: 'Account',
			appearance: 'Appearance',
			notifications: 'Notifications',
			billing: 'Billing',
			display: 'Display',
		},
		appearance: {
			description:
				'Theme and style for the private {{brandKey}} workspace. Public surfaces keep the brand default.',
			sections: {
				theme: 'Theme mode',
				stylePreset: 'Style preset',
			},
			themeOptions: {
				system: {
					title: 'System',
					description: 'Follow your device preference.',
				},
				light: {
					title: 'Light',
					description: 'Always use the light workspace.',
				},
				dark: {
					title: 'Dark',
					description: 'Always use the dark workspace.',
				},
			},
			presetKinds: {
				default: 'Brand default',
				alternative: 'Variant',
			},
			errors: {
				invalidStylePreset: 'Select a valid style preset.',
			},
		},
		jurisdiction: {
			label: 'Catalog country',
			placeholder: 'Select country',
			help: 'Used to prioritize national content when available.',
			submit: 'Save catalog country',
		},
		messages: {
			accountUpdated: 'Account settings updated successfully.',
			profileUpdated: 'Profile updated successfully.',
			notificationsUpdated: 'Notification preferences updated successfully.',
			displayUpdated: 'Display settings updated successfully.',
			appearanceUpdated: 'Appearance settings updated successfully.',
			accountUpdateFailed: 'Failed to update account settings.',
			profileUpdateFailed: 'Failed to update profile settings.',
		},
		profile: {
			title: 'Profile',
			description:
				'Update your profile information. This information can be shown publicly.',
			valueUnavailable: 'Not available',
			planFree: 'Free',
			planPro: 'Pro',
			basicInfo: {
				title: 'Basic information',
				description:
					'Reference details from your account. These fields are not editable here.',
				displayName: 'Display name',
				username: 'Username',
				email: 'Email',
				memberSince: 'Member since',
				plan: 'Plan',
			},
			form: {
				displayNameLabel: 'Display name',
				displayNamePlaceholder: 'Your display name',
				bioLabel: 'Bio',
				bioPlaceholder: 'Tell us a bit about yourself',
				bioHint:
					'You can @mention other users and organizations to link to them.',
				avatarUrlLabel: 'Avatar URL',
				avatarUrlPlaceholder: 'https://example.com/avatar.jpg',
				submit: 'Update profile',
			},
		},
	},
	all: 'All',
	home: 'Home',
	about: 'About',
	cancel: 'Cancel',
	yes: 'Yes',
	no: 'No',
	loading: 'Loading…',
	processing: 'Processing…',
	footer: {
		copyright: '  {{year}} {{brandName}}. All rights reserved.',
	},
	cookies: {
		message: 'This website uses cookies to enhance user experience. See our',
		privacyPolicy: 'Privacy Policy',
		forMoreInfo: 'for more information.',
		accept: 'Accept',
		decline: 'Decline',
	},
	error: {
		title: 'Error',
		unexpected: 'Oops! Something went wrong.',
		unknown: 'Unknown error',
		noDetails: 'No additional details available.',
		dialogNotFound: 'Dialog "{{dialogName}}" not found',
		boundary: {
			databaseError:
				'We encountered a database error. Please try again or contact support if the problem persists.',
			serverError:
				'Something went wrong on our end. Our team has been notified. Please try again later.',
			validationError:
				'The information provided is invalid. Please check your input and try again.',
			defaultError:
				'An unexpected error occurred. Please try again or contact support if the problem continues.',
			showTechnical: 'Show Technical Details',
			showFriendly: 'Show Friendly Message',
		},
	},
	notFound: {
		title: 'Error 404',
		message: "Sorry, we can't find this page",
	},
	permissionDenied: {
		title: 'Error 403',
		message: "Sorry, but you don't have permission.",
	},
	backToHome: 'Back to home',
	toggle: 'Toggle',
	selected: 'selected',
	confirmDelete: 'Are you sure you want to delete these items?',
	success: 'Success',
	fields: {
		createdAt: 'Created',
		updatedAt: 'Updated',
	},
	table: {
		noResults: 'No results found',
		search: 'Search...',
		filter: 'Filter elements',
		rows: 'Rows',
		rowsPerPage: 'Rows per page',
		goTo: 'Go to',
		page: 'Page',
		of: 'of',
		previous: 'Previous',
		next: 'Next',
		first: 'First',
		last: 'Last',
		actions: 'Actions',
		filters: {
			all: 'All',
			active: 'Active',
			inactive: 'Inactive',
			published: 'Published',
			draft: 'Draft',
		},
		sort: {
			asc: 'Sort ascending',
			desc: 'Sort descending',
			none: 'No sorting',
		},
	},
	buttons: {
		create: 'Create',
	},
	actions: {
		create: 'Create',
		edit: 'Edit',
		delete: 'Delete',
		suspendAccount: 'Suspend account',
		reactivateAccount: 'Reactivate account',
		hardDeleteAccount: 'Hard delete account',
		impersonate: 'Impersonate',
		view: 'View',
		save: 'Save',
		saving: 'Saving...',
		cancel: 'Cancel',
		confirm: 'Confirm',
		close: 'Close',
		back: 'Back',
		next: 'Next',
		previous: 'Previous',
		expand: 'Expand',
		collapse: 'Collapse',
		submit: 'Submit',
		reset: 'Reset',
		clear: 'Clear',
		search: 'Search',
		filter: 'Filter',
		export: 'Export',
		import: 'Import',
		refresh: 'Refresh',
		retry: 'Retry',
		duplicate: 'Duplicate',
		archive: 'Archive',
		restore: 'Restore',
		publish: 'Publish',
		unpublish: 'Unpublish',
		toggleFavorite: 'Add to favorites',
		toggleUnfavorite: 'Remove from favorites',
		togglePinned: 'Pin',
		toggleUnpinned: 'Unpin',
		bookmarkResource: 'Save content',
		markCompleted: 'Mark completed',
		markIncomplete: 'Mark incomplete',
	},
	views: {
		list: 'List',
		grid: 'Grid',
		kanban: 'Kanban',
	},
	status: {
		active: 'Active',
		inactive: 'Inactive',
		published: 'Published',
		draft: 'Draft',
		archived: 'Archived',
		pending: 'Pending',
		approved: 'Approved',
		rejected: 'Rejected',
		completed: 'Completed',
		inProgress: 'In Progress',
		notStarted: 'Not Started',
		bookmarked: 'Saved',
		suspended: 'Suspended',
	},
	filters: {
		favorites: 'Favorites',
		bookmarks: 'Saved',
	},
	messages: {
		success: {
			created: '{{entity}} created successfully',
			updated: '{{entity}} updated successfully',
			deleted: '{{entity}} deleted successfully',
			saved: 'Changes saved successfully',
			published: '{{entity}} published successfully',
			unpublished: '{{entity}} unpublished successfully',
		},
		error: {
			create: 'Failed to create {{entity}}',
			update: 'Failed to update {{entity}}',
			delete: 'Failed to delete {{entity}}',
			save: 'Failed to save changes',
			load: 'Failed to load data',
			network: 'Network error occurred',
			validation: 'Please check your input and try again',
			permission: "You don't have permission to perform this action",
		},
		confirm: {
			delete: 'Are you sure you want to delete this {{entity}}?',
			archive: 'Are you sure you want to archive this {{entity}}?',
			publish: 'Are you sure you want to publish this {{entity}}?',
			unpublish: 'Are you sure you want to unpublish this {{entity}}?',
		},
	},
	validation: {
		required: 'This field is required',
		positiveNumber: 'Must be a positive number',
	},
	collections: {
		title: '{{collectionEntityLabelPlural}}',
		description:
			'Explore {{collectionEntityLabelPluralLower}} built from {{mainEntityLabelPluralLower}}',
		myCollections: 'My {{collectionEntityLabelPlural}}',
		myCollectionsDescription:
			'Manage your {{collectionEntityLabelPluralLower}}',
		sequential: 'Sequential',
		noCollections: 'No {{collectionEntityLabelPluralLower}} available',
		noCollectionsYet:
			"You don't have any {{collectionEntityLabelPluralLower}} yet",
		createFirstCollection:
			'Create your first {{collectionEntityLabelLower}} to get started',
		noCourses:
			'No {{mainEntityLabelPluralLower}} in this {{collectionEntityLabelLower}} yet',
		listView: 'List View',
		diagramView: 'Diagram',
		diagramComingSoon: 'Diagram view coming soon',
		diagramDescription:
			'Visualize this {{collectionEntityLabelLower}} with an interactive diagram',
		collectionsCarousel: {
			title: 'Featured {{collectionEntityLabelPlural}}',
			subtitle:
				'Curated {{collectionEntityLabelPluralLower}} to grow your skills',
			viewAll: 'Explore all {{collectionEntityLabelPluralLower}}',
			startFree: 'Start for free',
			viewFreeCollection: 'View free {{collectionEntityLabelLower}}',
			viewCollection: 'View {{collectionEntityLabelLower}}',
			previousSlide: 'Previous slide',
			nextSlide: 'Next slide',
			goToSlide: 'Go to slide {{slide}}',
			previewAlt:
				'Preview of {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
			modules_one: '{{count}} module',
			modules_other: '{{count}} modules',
			courses_one: '{{count}} {{mainEntityLabelLower}}',
			courses_other: '{{count}} {{mainEntityLabelPluralLower}}',
			routes_one: '{{count}} {{collectionEntityLabelLower}}',
			routes_other: '{{count}} {{collectionEntityLabelPluralLower}}',
		},
	},
	studyPanel: {
		title: 'Study panel',
		searchPlaceholder:
			'Search {{collectionEntityLabelPluralLower}} and {{mainEntityLabelPluralLower}}',
		scope: {
			panel: 'My library',
			catalog: '{{brandShortName}} catalog',
		},
		catalog: {
			emptyDashboard: {
				title: 'Start by exploring the catalog',
				description:
					'After you enroll in a {{mainEntityLabelLower}} or {{collectionEntityLabelLower}}, this note disappears and My panel becomes your working view.',
			},
			empty: {
				title: 'No catalog results',
				description: 'Try another search or change the active filters.',
			},
			table: {
				learning: 'Content',
				structure: 'Structure',
				action: 'Action',
			},
			belongsTo: 'Inside',
			contextOnlyProgram: 'Context',
			structure: {
				program: '{{count}} {{mainEntityLabelPluralLower}}',
				course: '{{count}} topics',
			},
			actions: {
				enrollProgram: 'Start this {{collectionEntityLabelLower}}',
				enrollCourse: 'Start this {{mainEntityLabelLower}}',
				enrollCourseOnly: 'Enroll only in this {{mainEntityLabelLower}}',
			},
		},
		badges: {
			course: '{{mainEntityLabel}}',
			roadmap: '{{collectionEntityLabel}}',
			sequential: 'Sequential',
			goalOriented: 'Goal-oriented',
			exploratory: 'Exploratory',
			certificate: 'Certificate',
		},
		views: {
			list: 'List',
			grid: 'Grid',
		},
		actions: {
			open: 'Open',
			details: 'Details',
		},
		deleteConfirm: {
			courseTitle: 'Delete this {{mainEntityLabelLower}}?',
			courseDescription:
				'"{{title}}" will be permanently deleted. This action cannot be undone.',
			courseImpact:
				'All enrollments and progress tied to this {{mainEntityLabel}} will be removed.',
			collectionTitle: 'Delete {{collectionEntityLabel}}?',
			collectionDescription:
				'"{{title}}" will be permanently deleted. This action cannot be undone.',
			collectionDescriptionWithCounts:
				'"{{title}}" will be permanently deleted, including {{courseCount}} linked {{mainEntityLabelPluralLower}}.',
			collectionImpact:
				'The enrollments tied to this {{collectionEntityLabel}} will be removed and you will lose access.',
			roadmapTitle: 'Remove {{collectionEntityLabel}} from your panel?',
			roadmapDescription: '"{{title}}" will be removed from your study panel.',
			roadmapImpact: 'You can enroll again later, if you wish.',
			roadmapConfirmAction: 'Remove from panel',
			confirmAction: 'Delete permanently',
		},
		filters: {
			all: 'All',
			allTypes: 'All types',
			courses: '{{mainEntityLabelPluralLower}}',
			roadmaps: '{{collectionEntityLabelPlural}}',
			allCategories: 'All categories',
			allProgress: 'All progress',
			notStarted: 'Not started',
			inProgress: 'In progress',
			paused: 'Paused',
			completed: 'Completed',
			archived: 'Archived',
		},
		units: {
			modules_one: '{{count}} module',
			modules_other: '{{count}} modules',
			topics_one: '{{count}} topic',
			topics_other: '{{count}} topics',
			courses_one: '{{count}} {{mainEntityLabelLower}}',
			courses_other: '{{count}} {{mainEntityLabelPluralLower}}',
		},
		table: {
			item: 'Name',
			type: 'Type',
			status: 'Status',
			category: 'Category',
			lastActivity: 'Last\u00A0activity',
			deadline: 'Deadline',
			progress: 'Progress',
			enrolledAt: 'Enrolled',
		},
		drawer: {
			description: 'Expanded context and quick controls for your study item.',
			progress: 'Progress',
			enrolledAt: 'Enrolled',
			learningUnits: 'Learning units',
			category: 'Category',
			completedCourses: 'Completed {{mainEntityLabelPluralLower}}',
			childCourses:
				'{{mainEntityLabelPlural}} in this {{collectionEntityLabelLower}}',
			moreCourses: 'more',
			roadmapStatus: '{{collectionEntityLabel}} status',
			updateRoadmapStatus: 'Update status',
			courseActions: '{{mainEntityLabel}} actions',
		},
		empty: {
			title: 'No matching study items',
			description:
				'Try changing filters or search terms to find your {{mainEntityLabelPluralLower}} and {{collectionEntityLabelPluralLower}}.',
		},
	},
	collectionDetail: {
		hero: {
			guidedLabel: 'Guided {{collectionEntityLabelLower}}',
			referenceLabel: 'Reference {{collectionEntityLabelLower}}',
			estimatedHours: '{{count}} estimated hours',
		},
		actions: {
			backToDashboard: 'Back to dashboard',
			toggleMode: 'Switch learning mode',
			enroll: 'Enroll in this {{collectionEntityLabelLower}}',
			enrollCta: 'Enroll',
			startGoalPath: 'Start your {{collectionEntityLabelLower}}',
			continueGoalPath: 'Continue your {{collectionEntityLabelLower}}',
			start: 'Start',
			continue: 'Continue',
			review: 'Review',
			pause: 'Pause',
			showTopics: 'Show topics',
			hideTopics: 'Hide topics',
		},
		badges: {
			enrolled: 'Enrolled',
		},
		modes: {
			goalOriented: 'Goal-oriented',
			exploratory: 'Exploratory',
		},
		views: {
			grid: 'Grid',
			roadmap: 'Roadmap',
		},
		roadmap: {
			recommended: 'Recommended next',
			moduleProgress:
				'{{completed}}/{{total}} {{mainEntityLabelPluralLower}} completed',
		},
		modeDescriptions: {
			goalOriented:
				'Follow a structured path with a recommended sequence to reach a clear learning goal.',
			exploratory:
				'Discover topics freely and choose the order that best matches your curiosity and needs.',
		},
		metrics: {
			progress: 'Progress',
			courses: '{{mainEntityLabelPlural}}',
			topics: 'Topics',
			modules: 'Modules',
			stages: 'stages',
			topicProgress: '{{completed}}/{{total}} topics completed',
			noTopics: 'No topics yet',
		},
		resume: {
			continueEyebrow: 'Pick up where you left off',
			startEyebrow: 'Start your {{collectionEntityLabelLower}}',
		},
		status: {
			not_enrolled: 'Not enrolled',
			not_started: 'Not started',
			in_progress: 'In progress',
			paused: 'Paused',
			completed: 'Completed',
		},
		empty: {
			noCourses:
				'No {{mainEntityLabelPluralLower}} available in this {{collectionEntityLabelLower}} yet.',
		},
		finalCta: {
			title: 'Ready to start?',
			subtitleLoggedIn: 'Enroll in {{collection}} to begin right away.',
			titleEnrolled: 'Continue your {{collectionEntityLabelLower}}',
			subtitleEnrolled:
				'Jump back into {{collection}} and keep moving forward.',
			enrollButton:
				'Start {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
			enrollButtonAnonymous: 'Create your account and start here',
			anonymousHint:
				'It is free. We will ask you to create an account to save your progress.',
			continueButton:
				'Continue {{collectionEntityArticleLower}} {{collectionEntityLabelLower}}',
		},
		actionError: {
			title: "We couldn't start this",
		},
		fallbackCollectionTitle: 'Untitled {{collectionEntityLabelLower}}',
		fallbackModuleTitle: 'Untitled module',
		fallbackCourseTitle: 'Untitled {{mainEntityLabelLower}}',
		fallbackTopicTitle: 'Untitled topic',
		fallbackDescription:
			'No description available for this {{collectionEntityLabelLower}}.',
		fallbackCourseDescription:
			'No description available for this {{mainEntityLabelLower}}.',
	},
	upgradeAction: {
		limitReached: {
			title: 'You have used your included access',
			description:
				'Your library is still available. To open new {{mainEntityLabelPluralLower}}, review your Pro access.',
			primaryCta: 'Review my access',
		},
	},
	courses: '{{mainEntityLabelPluralLower}}',
	students: 'students',
	section: 'Section',
	module: 'Module',
	stage: 'Stage',
	step: 'Step',
	viewDetails: 'View details',
	viewCourse: 'View {{mainEntityLabelLower}}',
	view: 'View',
	open: 'Open',
	draft: 'Draft',
	convert: 'Convert',
	languages: {
		en: 'English',
		es: 'Spanish',
		fr: 'French',
		de: 'German',
		it: 'Italian',
		pt: 'Portuguese',
		english: 'English',
		spanish: 'Spanish',
		french: 'French',
		german: 'German',
		italian: 'Italian',
		portuguese: 'Portuguese',
	},
	levels: {
		beginner: 'Beginner',
		intermediate: 'Intermediate',
		advanced: 'Advanced',
		expert: 'Expert',
		all: 'All levels',
	},
	accessLevels: {
		free: 'Free',
		subscription: 'Subscription',
		enrollment: 'Enrollment',
		preview: 'Preview',
	},
	i18n: {
		settings: {
			defaultLanguage: 'Default language',
			supportedLngs: 'Supported languages',
		},
	},
	subscription: {
		proOnly: 'Available on the Pro plan',
	},
	impersonation: {
		banner: 'Impersonating {{target}}',
		exit: 'Exit impersonation',
	},
	editor: {
		placeholder: 'Type your content here...',
	},
	courseMap: {
		insertionPoint: {
			createTopic: 'Create new topic',
			searchEntity: 'Search existing content',
		},
		module: {
			rename: 'Rename module',
			toggle: 'Expand or collapse module',
			createNew: 'Add new module',
		},
		topic: {
			completeTopic: 'Complete topic',
			label: 'Topic',
			markCompleted: 'Mark as completed',
			markNotStarted: 'Mark as not started',
			rename: 'Rename topic',
		},
	},
	tour: {
		next: 'Next',
		previous: 'Back',
		done: 'Got it',
		progressSeparator: 'of',
		dashboard: {
			scope: {
				title: 'Your panel and the catalog',
				description:
					'Switch between your personal study panel and the {{mainEntityLabelLower}} catalog from here.',
			},
			libraryScope: {
				title: 'My library',
				description:
					'Search here when you want to find the {{collectionEntityLabelPluralLower}} and {{mainEntityLabelPluralLower}} already in your learning space.',
			},
			catalogScope: {
				title: '{{brandShortName}} catalog',
				description:
					'Search here to discover new {{mainEntityLabelPluralLower}} and {{collectionEntityLabelPluralLower}} from the full catalog.',
			},
			toolbar: {
				title: 'Search and filter',
				description:
					'Quickly find your {{mainEntityLabelPluralLower}} and {{collectionEntityLabelPluralLower}} by text, type, status, or category.',
			},
			create: {
				title: 'Create your own',
				description:
					'Build your own {{mainEntityLabelLower}} whenever you need something custom.',
			},
			sidebarNav: {
				title: 'Main navigation',
				description:
					'The sidebar takes you to your library, the catalog, and the tools.',
			},
			sidebarSupport: {
				title: 'Support and this tour',
				description:
					'Documentation, contact, and the option to replay this tour live here.',
			},
		},
		course: {
			header: {
				title: 'At a glance',
				description:
					'The header shows the title of this {{mainEntityLabelLower}}, your progress, and the main actions.',
			},
			index: {
				title: 'The study map',
				description:
					'This index organizes the {{mainEntityLabelLower}} into modules and topics. It is your study map.',
			},
			module: {
				title: 'Modules',
				description: 'Click a module to expand or collapse its topics.',
			},
			topicRow: {
				title: 'Topics',
				description:
					'Open a topic to study it: contents, notes, diagrams, and resources.',
			},
			topicComplete: {
				title: 'Track your progress',
				description:
					'Mark each topic as completed to keep track of how far you have come.',
			},
			topicActions: {
				title: 'Topic actions',
				description:
					'Hover over a topic to reveal this menu with its actions: create content, collect resources, and more.',
			},
			topicMeta: {
				title: 'Topic indicators',
				description:
					'These badges show what each topic holds: contents, diagrams, and resources.',
			},
			moduleInsertion: {
				title: 'Add or reuse',
				description:
					'Each module ends with two buttons: create a new topic or search existing topics and {{mainEntityLabelPluralLower}} to link them.',
			},
		},
	},
}
